import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface Company {
  id: string
  name: string
  description: string | null
  website: string | null
  logoUrl: string | null
  createdAt: string
  updatedAt: string
  // New fields from Epic 18 Story 2
  employeeCount: number | null
  revenueRange: string | null
  businessBranches: string | null
  aiPainPoints: string | null
  aiAdoptionStage: string | null
}

interface ApiResponse {
  company: Company
}

export default function CompanyProfile() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [company, setCompany] = useState<Company | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<{
    employeeCount?: string
    aiPainPoints?: string
    businessBranches?: string
  }>({})

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    logoUrl: '',
    // New fields from Epic 18 Story 2
    employeeCount: '',
    revenueRange: '',
    businessBranches: '',
    aiPainPoints: '',
    aiAdoptionStage: ''
  })

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const currentUser = userStr ? JSON.parse(userStr) : null

  // Get companyId from URL query parameter (for super admins)
  const queryCompanyId = searchParams.get('companyId')
  const isSuperAdmin = currentUser?.role === 'super_admin'

  useEffect(() => {
    if (!currentUser) {
      navigate('/login')
      return
    }

    // Check if user is company admin or super admin
    if (currentUser.role !== 'company_admin' && currentUser.role !== 'super_admin') {
      navigate('/dashboard')
      return
    }

    // Super admins must provide companyId query parameter
    if (isSuperAdmin && !queryCompanyId) {
      setError('Company ID is required for super admins. Please select a company from the companies list.')
      setLoading(false)
      return
    }

    fetchCompanyProfile()
  }, [navigate, currentUser, isSuperAdmin, queryCompanyId])

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')

      // Build URL with query parameter for super admins
      let url = 'http://localhost:3000/api/company/profile'
      if (isSuperAdmin && queryCompanyId) {
        url += `?companyId=${encodeURIComponent(queryCompanyId)}`
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/login')
          return
        }
        throw new Error('Failed to fetch company profile')
      }

      const data: ApiResponse = await response.json()
      setCompany(data.company)
      setFormData({
        name: data.company.name || '',
        description: data.company.description || '',
        website: data.company.website || '',
        logoUrl: data.company.logoUrl || '',
        // New fields from Epic 18 Story 2
        employeeCount: data.company.employeeCount?.toString() || '',
        revenueRange: data.company.revenueRange || '',
        businessBranches: data.company.businessBranches || '',
        aiPainPoints: data.company.aiPainPoints || '',
        aiAdoptionStage: data.company.aiAdoptionStage || ''
      })
      if (data.company.logoUrl) {
        setLogoPreview(data.company.logoUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setSuccess(false)

    // Clear validation error for this field when user starts typing
    setValidationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[name as keyof typeof prev]
      return newErrors
    })

    // Validate employeeCount on input
    if (name === 'employeeCount' && value !== '') {
      const count = parseInt(value, 10)
      if (isNaN(count) || count < 0) {
        setValidationErrors(prev => ({
          ...prev,
          employeeCount: 'Employee count must be a non-negative number'
        }))
      }
    }

    // Validate aiPainPoints character limit
    if (name === 'aiPainPoints' && value.length > 2000) {
      setValidationErrors(prev => ({
        ...prev,
        aiPainPoints: 'AI challenges must be 2000 characters or less'
      }))
    }
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB')
        return
      }

      setLogoFile(file)
      setError(null)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = async () => {
    if (!logoFile) return null

    try {
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('file', logoFile)
      formData.append('category', 'images')

      const response = await fetch('http://localhost:3000/api/content/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to upload logo')
      }

      const data = await response.json()
      return data.filePath
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo')
      return null
    }
  }

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {}

    // Validate employeeCount
    if (formData.employeeCount !== '') {
      const count = parseInt(formData.employeeCount, 10)
      if (isNaN(count) || count < 0) {
        errors.employeeCount = 'Employee count must be a non-negative number'
      }
    }

    // Validate aiPainPoints character limit
    if (formData.aiPainPoints.length > 2000) {
      errors.aiPainPoints = 'AI challenges must be 2000 characters or less'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(false)

    try {
      // Upload logo if a new one was selected
      let logoUrl = formData.logoUrl
      if (logoFile) {
        const uploadedLogoUrl = await handleLogoUpload()
        if (uploadedLogoUrl) {
          logoUrl = uploadedLogoUrl
        } else {
          setSaving(false)
          return
        }
      }

      const token = localStorage.getItem('token')

      // Build URL with query parameter for super admins
      let url = 'http://localhost:3000/api/company/profile'
      if (isSuperAdmin && queryCompanyId) {
        url += `?companyId=${encodeURIComponent(queryCompanyId)}`
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          website: formData.website || null,
          logoUrl: logoUrl || null,
          // New fields from Epic 18 Story 2
          employeeCount: formData.employeeCount ? parseInt(formData.employeeCount, 10) : null,
          revenueRange: formData.revenueRange || null,
          businessBranches: formData.businessBranches || null,
          aiPainPoints: formData.aiPainPoints || null,
          aiAdoptionStage: formData.aiAdoptionStage || null
        })
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/login')
          return
        }

        // Try to parse validation errors from backend
        const errorData = await response.json().catch(() => null)
        if (errorData?.errors) {
          // Backend returned validation errors
          const apiErrors: typeof validationErrors = {}
          errorData.errors.forEach((err: { field: string; message: string }) => {
            // Map backend field names to frontend field names
            const fieldName = err.field.replace('employee_count', 'employeeCount')
                                   .replace('ai_pain_points', 'aiPainPoints')
                                   .replace('business_branches', 'businessBranches')
                                   .replace('revenue_range', 'revenueRange')
                                   .replace('ai_adoption_stage', 'aiAdoptionStage')
            apiErrors[fieldName as keyof typeof apiErrors] = err.message
          })
          setValidationErrors(apiErrors)
          setError('Please fix the validation errors below')
          setSaving(false)
          return
        }

        throw new Error(errorData?.message || 'Failed to update company profile')
      }

      const data: ApiResponse = await response.json()
      setCompany(data.company)
      setFormData({
        name: data.company.name || '',
        description: data.company.description || '',
        website: data.company.website || '',
        logoUrl: data.company.logoUrl || '',
        // New fields from Epic 18 Story 2
        employeeCount: data.company.employeeCount?.toString() || '',
        revenueRange: data.company.revenueRange || '',
        businessBranches: data.company.businessBranches || '',
        aiPainPoints: data.company.aiPainPoints || '',
        aiAdoptionStage: data.company.aiAdoptionStage || ''
      })
      setLogoFile(null)
      setSuccess(true)

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (company) {
      setFormData({
        name: company.name || '',
        description: company.description || '',
        website: company.website || '',
        logoUrl: company.logoUrl || '',
        // New fields from Epic 18 Story 2
        employeeCount: company.employeeCount?.toString() || '',
        revenueRange: company.revenueRange || '',
        businessBranches: company.businessBranches || '',
        aiPainPoints: company.aiPainPoints || '',
        aiAdoptionStage: company.aiAdoptionStage || ''
      })
      setLogoPreview(company.logoUrl || null)
      setLogoFile(null)
    }
    setError(null)
    setSuccess(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-orange-500"></div>
          <p className="mt-2 text-muted-foreground">Loading company profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        {/* Back button for super admins */}
        {isSuperAdmin && (
          <button
            onClick={() => navigate('/admin/companies')}
            className="mb-4 flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Companies List
          </button>
        )}
        <h1 className="text-3xl font-bold text-foreground mb-2">Company Profile</h1>
        <p className="text-muted-foreground">Manage your company information and branding</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/50 rounded-lg">
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-green-600 dark:text-green-400">Company profile updated successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload Section */}
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Company Logo</h2>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Company logo"
                  className="w-32 h-32 object-contain rounded-lg border-2 border-border"
                />
              ) : (
                <div className="w-32 h-32 bg-muted rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                  <svg className="w-12 h-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="block mb-2 text-sm font-medium text-foreground">
                Upload Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  dark:file:bg-orange-50 dark:file:text-orange-700
                  hover:file:bg-blue-100 dark:hover:file:bg-orange-100
                  cursor-pointer"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Recommended: Square image, at least 200x200px. Max file size: 5MB.
              </p>
            </div>
          </div>
        </div>

        {/* Company Information Section */}
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Company Information</h2>

          {/* Company Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-foreground">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              maxLength={255}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:border-transparent bg-background text-foreground"
              placeholder="Enter company name"
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block mb-2 text-sm font-medium text-foreground">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              maxLength={1000}
              rows={4}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:border-transparent bg-background text-foreground resize-none"
              placeholder="Describe your company, mission, and values"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {formData.description.length} / 1000 characters
            </p>
          </div>

          {/* Website */}
          <div className="mb-4">
            <label htmlFor="website" className="block mb-2 text-sm font-medium text-foreground">
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              maxLength={255}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:border-transparent bg-background text-foreground"
              placeholder="https://example.com"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Include the full URL (e.g., https://www.example.com)
            </p>
          </div>
        </div>

        {/* Business Details Section */}
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Business Details</h2>

          {/* Employee Count */}
          <div className="mb-4">
            <label htmlFor="employeeCount" className="block mb-2 text-sm font-medium text-foreground">
              Number of Employees
            </label>
            <input
              type="number"
              id="employeeCount"
              name="employeeCount"
              value={formData.employeeCount}
              onChange={handleInputChange}
              min="0"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-background text-foreground ${
                validationErrors.employeeCount
                  ? 'border-red-500 focus:ring-red-500 dark:focus:ring-red-400'
                  : 'border-input focus:ring-blue-500 dark:focus:ring-orange-500'
              }`}
              placeholder="e.g., 50"
            />
            {validationErrors.employeeCount && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {validationErrors.employeeCount}
              </p>
            )}
            {!validationErrors.employeeCount && (
              <p className="mt-1 text-xs text-muted-foreground">
                Total number of employees in your company
              </p>
            )}
          </div>

          {/* Revenue Range */}
          <div className="mb-4">
            <label htmlFor="revenueRange" className="block mb-2 text-sm font-medium text-foreground">
              Annual Revenue Range
            </label>
            <select
              id="revenueRange"
              name="revenueRange"
              value={formData.revenueRange}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:border-transparent bg-background text-foreground"
            >
              <option value="">Select revenue range</option>
              <option value="under-1M">Under $1M</option>
              <option value="1M-5M">$1M - $5M</option>
              <option value="5M-10M">$5M - $10M</option>
              <option value="10M-50M">$10M - $50M</option>
              <option value="50M-100M">$50M - $100M</option>
              <option value="100M+">$100M+</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Approximate annual revenue (helps us understand your company size)
            </p>
          </div>

          {/* AI Adoption Stage */}
          <div className="mb-4">
            <label htmlFor="aiAdoptionStage" className="block mb-2 text-sm font-medium text-foreground">
              AI Adoption Stage
            </label>
            <select
              id="aiAdoptionStage"
              name="aiAdoptionStage"
              value={formData.aiAdoptionStage}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:border-transparent bg-background text-foreground"
            >
              <option value="">Select adoption stage</option>
              <option value="exploring">Exploring - Researching AI possibilities</option>
              <option value="piloting">Piloting - Testing AI in small projects</option>
              <option value="implementing">Implementing - Using AI in some processes</option>
              <option value="scaling">Scaling - Expanding AI across the organization</option>
              <option value="mature">Mature - AI is integral to business operations</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Current stage of AI adoption in your company
            </p>
          </div>
        </div>

        {/* AI Needs & Use Cases Section */}
        <div className="bg-card border border-border rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">AI Needs & Use Cases</h2>

          {/* Business Branches */}
          <div className="mb-4">
            <label htmlFor="businessBranches" className="block mb-2 text-sm font-medium text-foreground">
              Business Areas & Branches
            </label>
            <textarea
              id="businessBranches"
              name="businessBranches"
              value={formData.businessBranches}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-background text-foreground resize-none ${
                validationErrors.businessBranches
                  ? 'border-red-500 focus:ring-red-500 dark:focus:ring-red-400'
                  : 'border-input focus:ring-blue-500 dark:focus:ring-orange-500'
              }`}
              placeholder="Enter your business areas or branches, one per line"
            />
            {validationErrors.businessBranches && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {validationErrors.businessBranches}
              </p>
            )}
            {!validationErrors.businessBranches && (
              <p className="mt-1 text-xs text-muted-foreground">
                List the different business areas, departments, or branches in your company (one per line or comma-separated)
              </p>
            )}
          </div>

          {/* AI Pain Points */}
          <div className="mb-4">
            <label htmlFor="aiPainPoints" className="block mb-2 text-sm font-medium text-foreground">
              AI Challenges & Use Cases
            </label>
            <textarea
              id="aiPainPoints"
              name="aiPainPoints"
              value={formData.aiPainPoints}
              onChange={handleInputChange}
              maxLength={2000}
              rows={6}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent bg-background text-foreground resize-none ${
                validationErrors.aiPainPoints
                  ? 'border-red-500 focus:ring-red-500 dark:focus:ring-red-400'
                  : 'border-input focus:ring-blue-500 dark:focus:ring-orange-500'
              }`}
              placeholder="Describe challenges where AI could help your company..."
            />
            <div className="mt-1">
              {validationErrors.aiPainPoints ? (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {validationErrors.aiPainPoints}
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    {formData.aiPainPoints.length} / 2000 characters
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    What business challenges are you facing? Where do you think AI could help? (e.g., automating customer support, analyzing large datasets, optimizing processes)
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Last Updated */}
        {company && (
          <div className="text-sm text-muted-foreground">
            Last updated: {new Date(company.updatedAt).toLocaleString()}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-orange-600 dark:hover:bg-orange-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 focus:ring-offset-2"
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="px-6 py-2 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
